import { FC, useState, useEffect } from 'react';
import { Box, Flex, Menu, Text, Accordion, AccordionItem, AccordionButton, AccordionPanel, useTheme } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';
import MenuButton from '../shared/MenuButton';
import ListItem from '../shared/ListItem';
import { StyledMenuList, ListItemsWrapper, StyledAccordionIcon, MobileListItem } from '../shared/StyledItems';
import useContextSwitcher from 'hooks/useContextSwitcher';

interface Props {
  items: ContextMenuItem[];
  current: PortalContext;
  isMobile?: boolean;
}

const ContextSwitcher: FC<Props> = ({ items, current, isMobile }) => {
  const [currentContext, setCurrentContext] = useState('');
  const [availableContext, setAvailableContext] = useState([] as ContextMenuItem[]);
  const { t } = useTranslation(['common']);
  const { switchContext } = useContextSwitcher();
  const { colors } = useTheme();

  useEffect(() => {
    setCurrentContext(items.find(({ value }) => value === current)?.title ?? '');
    setAvailableContext(items.filter(({ value }) => value !== current));
  }, [current]);

  if (isMobile) {
    return (
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton d="flex" justifyContent="space-between" alignItems="center" p="0 24px" sx={{ _hover: { background: 'none' } }}>
            <Flex flexDir="column" alignItems="start">
              <Text as="span" color={colors.gray[600]} fontSize="xs" lineHeight="14px">
                {t('contextSwitcher.contextLabel')}
              </Text>
              {currentContext}
            </Flex>
            <StyledAccordionIcon iconColor={colors.secondary[500]} />
          </AccordionButton>
          <AccordionPanel p="0">
            {availableContext.map(({ title, value }) => (
              <MobileListItem
                key={title}
                borderTop="1px solid"
                borderColor="secondary.300"
                justifyContent="start!important"
                _first={{
                  marginTop: '13px!important',
                }}
                _last={{
                  marginBottom: '7px!important',
                }}
                onClick={() => switchContext(value)}
              >
                {t('contextSwitcher.switchTo')}&nbsp;
                <Text as="span" fontWeight={500}>
                  {title}
                </Text>
              </MobileListItem>
            ))}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <Box>
      <Menu autoSelect={false} placement="bottom-end">
        <MenuButton rightIcon showMenuList>
          <Flex flexDir="column" alignItems="start">
            <Text as="span" fontSize="xs" fontWeight={400} color={rgba('white', 0.6)}>
              {t('contextSwitcher.contextLabel')}
            </Text>
            <Text as="span" color={rgba('white', 0.9)}>
              {currentContext}
            </Text>
          </Flex>
        </MenuButton>
        <StyledMenuList mt="12px" minW="1px">
          <ListItemsWrapper as="ul">
            {availableContext.map(({ title, value }) => (
              <ListItem as="li" key={title} _hover={{ cursor: 'pointer' }} onClick={() => switchContext(value)} justifyContent="start">
                {t('contextSwitcher.switchTo')}&nbsp;
                <Text as="span" fontWeight={500}>
                  {title}
                </Text>
              </ListItem>
            ))}
          </ListItemsWrapper>
        </StyledMenuList>
      </Menu>
    </Box>
  );
};

export default ContextSwitcher;
